/**
 * Theme Builder Utilities
 * 
 * This module provides utilities for creating a theme with light and dark color schemes.
 * It uses CSS variables for efficient theme switching.
 * All theme customization is done in this file.
 */

import { createTheme } from '@mui/material/styles';
import shadows from '@mui/material/styles/shadows';

/**
 * Creates a complete MUI theme with light and dark color schemes
 * @returns {Object} - Complete MUI theme object with CSS variables
 */
export function buildTheme() {
  // Define the theme with CSS variables and color schemes
  const theme = {
    // Enable CSS variables for the theme
    cssVariables: {
      colorSchemeSelector: 'data'
    },
    
    // Define color schemes for light and dark modes
    colorSchemes: {
      light: {
        palette: {
          // Primary brand color (Zignyl green) - matches zigy-mobile
          primary: {
            main: 'var(--color-green-400)',
            light: 'var(--color-green-300)',
            dark: 'var(--color-green-700)',
            contrastText: 'var(--color-white)'
          },
          
          // Secondary colors for accents and highlights - matches zigy-mobile overtime colors
          secondary: {
            main: '#D7702E', // Overtime color from TimecardHourInfo.jsx
            light: '#e08a52',
            dark: '#b85a1f',
            contrastText: 'var(--color-white)'
          },
          
          // Status and feedback colors
          success: {
            main: 'var(--color-green-400)',
            light: 'var(--color-green-300)',
            dark: 'var(--color-green-700)',
            contrastText: 'var(--color-white)'
          },
          
          warning: {
            main: '#D7702E', // Use consistent overtime/warning color from zigy-mobile
            light: '#e08a52',
            dark: '#b85a1f',
            contrastText: 'var(--color-white)'
          },
          
          error: {
            main: '#d32f2f',
            light: '#ef5350',
            dark: '#c62828',
            contrastText: 'var(--color-white)'
          },
          
          info: {
            main: '#0288d1',
            light: '#03a9f4',
            dark: '#01579b',
            contrastText: 'var(--color-white)'
          },
          
          // Neutral colors for text and backgrounds
          grey: {
            50: 'var(--color-grey-50)',
            100: 'var(--color-grey-100)',
            200: 'var(--color-grey-200)',
            300: 'var(--color-grey-300)',
            400: 'var(--color-grey-400)',
            500: 'var(--color-grey-500)',
            600: 'var(--color-grey-600)',
            700: 'var(--color-grey-700)',
            800: 'var(--color-grey-800)',
            900: 'var(--color-grey-900)'
          },
          
          // Background and text colors for light mode
          background: {
            default: 'var(--bg-page-default)',
            paper: 'var(--bg-panel)'
          },
          text: {
            primary: 'rgba(0, 0, 0, 0.87)',
            secondary: 'rgba(0, 0, 0, 0.6)',
            muted: 'var(--color-grey-600)'
          },
          
          // Custom theme variables for light mode
          gradient: 'linear-gradient(to right, var(--mui-palette-primary-main), var(--mui-palette-primary-light))',
          border: {
            subtle: 'var(--color-grey-200)',
            focus: 'var(--color-grey-300)',
            active: 'var(--color-grey-400)'
          },
          divider: {
            divider: 'var(--color-grey-200)',
          },
          surface: {
            hover: 'rgba(0, 0, 0, 0.04)',
            selected: 'var(--color-green-100)', // Light green for selected items
            disabled: 'rgba(0, 0, 0, 0.12)'
          },
          overlay: {
            light: 'rgba(255, 255, 255, 0.7)',
            medium: 'rgba(255, 255, 255, 0.5)',
            dark: 'rgba(0, 0, 0, 0.5)'
          }
        }
      },
      dark: {
        palette: {
          // Primary brand color (same as light mode)
          primary: {
            main: 'var(--color-green-400)',
            light: 'var(--color-green-300)',
            dark: 'var(--color-green-700)',
            contrastText: 'var(--color-white)'
          },
          
          // Secondary colors (same as light mode)
          secondary: {
            main: '#D7702E',
            light: '#e08a52',
            dark: '#b85a1f',
            contrastText: 'var(--color-white)'
          },
          
          // Status and feedback colors (same as light mode)
          success: {
            main: 'var(--color-green-400)',
            light: 'var(--color-green-300)',
            dark: 'var(--color-green-700)',
            contrastText: 'var(--color-white)'
          },
          
          warning: {
            main: '#D7702E',
            light: '#e08a52',
            dark: '#b85a1f',
            contrastText: 'var(--color-white)'
          },
          
          error: {
            main: '#d32f2f',
            light: '#ef5350',
            dark: '#c62828',
            contrastText: 'var(--color-white)'
          },
          
          info: {
            main: '#0288d1',
            light: '#03a9f4',
            dark: '#01579b',
            contrastText: 'var(--color-white)'
          },
          
          // Neutral colors (same as light mode)
          grey: {
            50: 'var(--color-grey-50)',
            100: 'var(--color-grey-100)',
            200: 'var(--color-grey-200)',
            300: 'var(--color-grey-300)',
            400: 'var(--color-grey-400)',
            500: 'var(--color-grey-500)',
            600: 'var(--color-grey-600)',
            700: 'var(--color-grey-700)',
            800: 'var(--color-grey-800)',
            900: 'var(--color-grey-900)',
            1000: 'var(--color-grey-1000)'
          },
          
          // Background and text colors for dark mode
          background: {
            default: 'var(--bg-page-default)',
            paper: 'var(--bg-panel)'
          },
          text: {
            primary: 'rgba(255, 255, 255, 0.87)',
            secondary: 'rgba(255, 255, 255, 0.6)',
            muted: 'var(--color-grey-400)'
          },
          
          // Custom theme variables for dark mode
          gradient: 'linear-gradient(to right, var(--mui-palette-primary-dark), var(--mui-palette-primary-main))',
          border: {
            subtle: 'var(--color-grey-900)',
            focus: 'var(--color-grey-800)',
            active: 'var(--color-grey-700)'
          },
          divider: {
            divider: 'var(--color-grey-900)',
          },
          surface: {
            hover: 'rgba(255, 255, 255, 0.05)',
            selected: 'var(--color-green-100)', // Light green for selected items (same as light mode)
            disabled: 'rgba(255, 255, 255, 0.15)'
          },
          overlay: {
            light: 'rgba(0, 0, 0, 0.7)',
            medium: 'rgba(0, 0, 0, 0.5)',
            dark: 'rgba(0, 0, 0, 0.3)'
          }
        }
      }
    },
    
    // Typography configuration
    typography: {
      fontFamily: [
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        '"Helvetica Neue"',
        'Arial',
        'sans-serif'
      ].join(','),
      
      // Font sizes for consistent scaling
      fontSize: 14,
      
      // Heading styles
      h1: {
        fontSize: '2.5rem',
        fontWeight: 600,
        lineHeight: 1.2
      },
      h2: {
        fontSize: '2rem',
        fontWeight: 600,
        lineHeight: 1.3
      },
      h3: {
        fontSize: '1.75rem',
        fontWeight: 600,
        lineHeight: 1.4
      },
      h4: {
        fontSize: '1.5rem',
        fontWeight: 600,
        lineHeight: 1.4
      },
      h5: {
        fontSize: '1.25rem',
        fontWeight: 600,
        lineHeight: 1.5
      },
      h6: {
        fontSize: '1rem',
        fontWeight: 600,
        lineHeight: 1.6
      },
      
      // Body text styles
      body1: {
        fontSize: '1rem',
        lineHeight: 1.5
      },
      body2: {
        fontSize: '0.875rem',
        lineHeight: 1.43
      },
      
      // Specialized text styles
      caption: {
        fontSize: '0.75rem',
        lineHeight: 1.66
      },
      overline: {
        fontSize: '0.75rem',
        fontWeight: 400,
        letterSpacing: '0.08333em',
        lineHeight: 2.66,
        textTransform: 'uppercase'
      }
    },
    
    // Spacing configuration (8px base unit)
    spacing: 8,
    
    // Component styling overrides
    components: {
      // DataGrid customizations (from App.css)
      MuiDataGrid: {
        styleOverrides: {
          root: {
            '& .MuiDataGrid-cell': {
              overflow: 'visible !important',
              position: 'relative'
            },
            '& .datagrid-mini-header': {
              background: '#f8f8f8', // Matches zigy-mobile DataGrid styling
              minHeight: '35px !important',
              maxHeight: '35px !important',
              '--height': '35px !important'
            },
            '& .datagrid-mini-header .MuiDataGrid-cell': {
              minHeight: '35px !important',
              maxHeight: '35px !important',
              height: '35px !important',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start'
            }
          }
        }
      },
      
      // Paper component styling (from styles.js)
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 'var(--radius-md)',
            backgroundImage: 'none', // removes default background-image
            boxShadow: 'none',
            border: 'var(--border-width) solid',
            borderColor: 'var(--color-border)', // or use theme.palette.divider
          }
        }
      },

      MuiPopover: {
        styleOverrides: {
            paper: {
              border: 'var(--border-width) solid',
              borderColor: 'var(--color-border)',
          },
        },
      },
        
      // Button styling
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 'var(--radius-md)',
            textTransform: 'none', // Preserve case
            fontWeight: 600
          }
        }
      },
      MuiBackdrop: {
        styleOverrides: {
          root: {
            top: 'var(--topnavbar-height)',   // 👈 shifts it down (e.g. below an AppBar)
            backgroundColor: 'var(--modal-backdrop-bg)',
          },
        },
      },

      // Alert component styling to fix green-on-green contrast issues
      MuiAlert: {
        styleOverrides: {
          standardSuccess: {
            // Use light green background with dark green text for proper contrast
            backgroundColor: 'light-dark(var(--color-green-100), var(--color-green-900))',
            color: 'light-dark(var(--color-green-700), var(--color-green-200))',
            border: '1px solid light-dark(var(--color-green-200), var(--color-green-800))',
            '& .MuiAlert-icon': {
              color: 'light-dark(var(--color-green-600), var(--color-green-400))',
            },
          },
        },
      },

    },
    
    // Shape configuration
    shape: {
      borderRadius: 8
    },
    
    // Shadow configuration
    shadows: [
      'none',
      '0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)',
      '0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)',
      '0px 3px 3px -2px rgba(0,0,0,0.2),0px 3px 4px 0px rgba(0,0,0,0.14),0px 1px 8px 0px rgba(0,0,0,0.12)',
      '0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)',
      '0px 3px 5px -1px rgba(0,0,0,0.2),0px 5px 8px 0px rgba(0,0,0,0.14),0px 1px 14px 0px rgba(0,0,0,0.12)',
      '0px 3px 5px -1px rgba(0,0,0,0.2),0px 6px 10px 0px rgba(0,0,0,0.14),0px 1px 18px 0px rgba(0,0,0,0.12)',
      '0px 4px 5px -2px rgba(0,0,0,0.2),0px 7px 10px 1px rgba(0,0,0,0.14),0px 2px 16px 1px rgba(0,0,0,0.12)',
      '0px 5px 5px -3px rgba(0,0,0,0.2),0px 8px 10px 1px rgba(0,0,0,0.14),0px 3px 14px 2px rgba(0,0,0,0.12)',
      '0px 5px 6px -3px rgba(0,0,0,0.2),0px 9px 12px 1px rgba(0,0,0,0.14),0px 3px 16px 2px rgba(0,0,0,0.12)',
      '0px 6px 6px -3px rgba(0,0,0,0.2),0px 10px 14px 1px rgba(0,0,0,0.14),0px 4px 18px 3px rgba(0,0,0,0.12)',
      '0px 6px 7px -4px rgba(0,0,0,0.2),0px 11px 15px 1px rgba(0,0,0,0.14),0px 4px 20px 3px rgba(0,0,0,0.12)',
      '0px 7px 8px -4px rgba(0,0,0,0.2),0px 12px 17px 2px rgba(0,0,0,0.14),0px 5px 22px 4px rgba(0,0,0,0.12)',
      '0px 7px 8px -4px rgba(0,0,0,0.2),0px 13px 19px 2px rgba(0,0,0,0.14),0px 5px 24px 4px rgba(0,0,0,0.12)',
      '0px 7px 9px -4px rgba(0,0,0,0.2),0px 14px 21px 2px rgba(0,0,0,0.14),0px 5px 26px 4px rgba(0,0,0,0.12)',
      '0px 8px 9px -5px rgba(0,0,0,0.2),0px 15px 22px 2px rgba(0,0,0,0.14),0px 6px 28px 5px rgba(0,0,0,0.12)',
      '0px 8px 10px -5px rgba(0,0,0,0.2),0px 16px 24px 2px rgba(0,0,0,0.14),0px 6px 30px 5px rgba(0,0,0,0.12)',
      '0px 8px 11px -5px rgba(0,0,0,0.2),0px 17px 26px 2px rgba(0,0,0,0.14),0px 6px 32px 5px rgba(0,0,0,0.12)',
      '0px 9px 11px -5px rgba(0,0,0,0.2),0px 18px 28px 2px rgba(0,0,0,0.14),0px 7px 34px 6px rgba(0,0,0,0.12)',
      '0px 9px 12px -6px rgba(0,0,0,0.2),0px 19px 29px 2px rgba(0,0,0,0.14),0px 7px 36px 6px rgba(0,0,0,0.12)',
      '0px 10px 13px -6px rgba(0,0,0,0.2),0px 20px 31px 3px rgba(0,0,0,0.14),0px 8px 38px 7px rgba(0,0,0,0.12)',
      '0px 10px 13px -6px rgba(0,0,0,0.2),0px 21px 33px 3px rgba(0,0,0,0.14),0px 8px 40px 7px rgba(0,0,0,0.12)',
      '0px 10px 14px -6px rgba(0,0,0,0.2),0px 22px 35px 3px rgba(0,0,0,0.14),0px 8px 42px 7px rgba(0,0,0,0.12)',
      '0px 11px 14px -7px rgba(0,0,0,0.2),0px 23px 36px 3px rgba(0,0,0,0.14),0px 9px 44px 8px rgba(0,0,0,0.12)',
      '0px 11px 15px -7px rgba(0,0,0,0.2),0px 24px 38px 3px rgba(0,0,0,0.14),0px 9px 46px 8px rgba(0,0,0,0.12)'
    ],
    
    // Breakpoints for responsive design
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1200,
        xl: 1536
      }
    }
  };
  
  // Create the final MUI theme with CSS variables
  return createTheme(theme);
}

/**
 * Gets the current theme mode from system preference or stored preference
 * @returns {boolean} - True if dark mode should be used
 */
export function getPreferredThemeMode() {
  // Check localStorage first
  const stored = localStorage.getItem('themeMode');
  if (stored) {
    return stored === 'dark';
  }
  
  // Fall back to system preference
  return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/**
 * Stores theme mode preference
 * @param {boolean} isDarkMode - Whether dark mode is preferred
 */
export function setThemeMode(isDarkMode) {
  localStorage.setItem('themeMode', isDarkMode ? 'dark' : 'light');
}
